import React, { useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useBlocker } from 'react-router-dom'

import { ClickNavigation, ClickNavigationProps } from '../../../../components/NavigationWithSideBar/ClickNavigation'
import { getIntegrationSettingsSchema, IntegrationSettings } from './schema'
import { FormSaveBar } from '../../../../components/FormSaveBar/FormSaveBar'
import { useGetIntegrationAdminSettings } from './hook'
import { DEFAULT_ADDITIONAL_OBJECTS_LIMIT, IntegrationServiceId } from '../../../../common/integrations'
import { DebugZodErrors } from './ZodValidator'
import { defaultCRMAccountIntegrationConfig } from './AdminSettingsTabs/model'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { DetailedCRMAccountIntegrationMapping, MappingType } from '../../../../models/Account'
import { areMappingsEqual } from '../../utils'

interface IntegrationAdminSettingsContainerProps {
  sections: ClickNavigationProps[]
  serviceId: IntegrationServiceId
}

export const IntegrationAdminSettingsContainer: React.FC<IntegrationAdminSettingsContainerProps> = ({ sections, serviceId }) => {
  const { isAccountUpdating, saveAccountMappingConfig, currentIntegrationAccountConfig } = useGetIntegrationAdminSettings(serviceId)
  const [formKey, setFormKey] = useState(0)

  const { getQuotaValue } = useZocksQuota()

  const additionalObjectsLimit = getQuotaValue('integration.additionalObjMax.value')
  const integrationSettingsSchema = getIntegrationSettingsSchema(additionalObjectsLimit as number)
  const methods = useForm<IntegrationSettings>({
    resolver: zodResolver(integrationSettingsSchema),
    mode: 'onChange',
    defaultValues: currentIntegrationAccountConfig || defaultCRMAccountIntegrationConfig(serviceId),
    shouldUnregister: false,
  })

  const {
    reset,
    control,
    getValues,
    setValue,
    formState: { isDirty, isSubmitting, isValid, errors },
  } = methods

  useEffect(() => {
    if (currentIntegrationAccountConfig) {
      reset(currentIntegrationAccountConfig)
    }
  }, [currentIntegrationAccountConfig, reset])

  const onSubmit = async (data: IntegrationSettings) => {
    await saveAccountMappingConfig(data)
    reset(data)
  }

  const handleDiscard = () => {
    reset()
    setFormKey((prevKey) => prevKey + 1)
  }

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !isSubmitting) {
        e.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty, isSubmitting])

  const blocker = useBlocker(({ currentLocation, nextLocation }) => isDirty && !isSubmitting && currentLocation.pathname !== nextLocation.pathname)

  const taskMapping = useWatch({ control, name: 'mappings.task' })
  const sessionMapping = useWatch({ control, name: 'mappings.session' })
  const additionalObjectsPath = 'mappings.additionalObjects'

  useEffect(() => {
    const customTaskMapping = taskMapping?.mappingType === MappingType.CUSTOM ? (taskMapping as DetailedCRMAccountIntegrationMapping) : null
    const customSessionMapping = sessionMapping?.mappingType === MappingType.CUSTOM ? (sessionMapping as DetailedCRMAccountIntegrationMapping) : null
    const additionalObjects = (getValues(additionalObjectsPath) as DetailedCRMAccountIntegrationMapping[]) || ([] as DetailedCRMAccountIntegrationMapping[])
    const filteredAdditionalObjects = additionalObjects.filter(
      (obj) => !obj?.additionalInformation?.isSessionObject && !obj?.additionalInformation?.isTaskObject,
    )

    if (!customTaskMapping && !customSessionMapping) {
      setValue(additionalObjectsPath, filteredAdditionalObjects, { shouldDirty: true, shouldValidate: true })
      return
    }

    const currentTaskObjectLimit = additionalObjects.find((obj) => obj?.additionalInformation?.isTaskObject)?.limit || DEFAULT_ADDITIONAL_OBJECTS_LIMIT
    const currentSessionObjectLimit = additionalObjects.find((obj) => obj?.additionalInformation?.isSessionObject)?.limit || DEFAULT_ADDITIONAL_OBJECTS_LIMIT

    const isSessionSameAsTask =
      customTaskMapping &&
      customSessionMapping &&
      customTaskMapping.objectName === customSessionMapping.objectName &&
      customTaskMapping.isHousehold == customSessionMapping.isHousehold

    if (!isSessionSameAsTask) {
      const currentTaskAdditionalObject = additionalObjects.find((obj) => obj?.additionalInformation?.isTaskObject)
      const currentSessionAdditionalObject = additionalObjects.find((obj) => obj?.additionalInformation?.isSessionObject)

      const updatedTaskAdditionalObject: DetailedCRMAccountIntegrationMapping | null = customTaskMapping
        ? {
            ...customTaskMapping,
            limit: currentTaskObjectLimit,
            additionalInformation: { ...(customTaskMapping?.additionalInformation || {}), isTaskObject: true },
          }
        : null

      const updatedSessionAdditionalObject: DetailedCRMAccountIntegrationMapping | null = customSessionMapping
        ? {
            ...customSessionMapping,
            limit: currentSessionObjectLimit,
            additionalInformation: { ...(customSessionMapping?.additionalInformation || {}), isSessionObject: true },
          }
        : null

      const shouldUpdate =
        !areMappingsEqual(currentTaskAdditionalObject, updatedTaskAdditionalObject) ||
        !areMappingsEqual(currentSessionAdditionalObject, updatedSessionAdditionalObject)

      if (!shouldUpdate) {
        return
      }

      const updatedAdditionalObjects = [
        ...filteredAdditionalObjects,
        ...(updatedTaskAdditionalObject ? [updatedTaskAdditionalObject] : []),
        ...(updatedSessionAdditionalObject ? [updatedSessionAdditionalObject] : []),
      ]
      setValue(additionalObjectsPath, updatedAdditionalObjects, { shouldDirty: true, shouldValidate: true })
      return
    }

    const currentSessionAndTaskObjectLimit =
      additionalObjects.find((obj) => obj?.additionalInformation?.isSessionObject && obj?.additionalInformation?.isTaskObject)?.limit ||
      DEFAULT_ADDITIONAL_OBJECTS_LIMIT

    const currentSessionAndTaskAdditionalObject = additionalObjects.find(
      (obj) => obj?.additionalInformation?.isSessionObject && obj?.additionalInformation?.isTaskObject,
    )
    const updatedSessionAndTaskAdditionalObject: DetailedCRMAccountIntegrationMapping = {
      ...customTaskMapping,
      limit: currentSessionAndTaskObjectLimit,
      additionalInformation: { ...(customTaskMapping?.additionalInformation || {}), isSessionObject: true, isTaskObject: true },
      fields: { ...customSessionMapping?.fields, ...(customTaskMapping?.fields || {}) },
      constraints: { ...customSessionMapping?.constraints, ...(customTaskMapping?.constraints || {}) },
    }
    if (areMappingsEqual(currentSessionAndTaskAdditionalObject, updatedSessionAndTaskAdditionalObject)) {
      return
    }
    const updatedAdditionalObjects = customTaskMapping ? [...filteredAdditionalObjects, updatedSessionAndTaskAdditionalObject] : filteredAdditionalObjects
    setValue(additionalObjectsPath, updatedAdditionalObjects, { shouldDirty: true, shouldValidate: true })
  }, [getValues, additionalObjectsPath, taskMapping, setValue, sessionMapping])

  return (
    <FormProvider {...methods}>
      <form id='integration-settings-form' onSubmit={methods.handleSubmit(onSubmit)} className='d-flex flex-column min-vh-100'>
        <div className='flex-grow-1 zocks-pb-10.0'>
          <ClickNavigation sections={sections} defaultActiveSectionId={sections[0].id} key={formKey} />
        </div>
        <FormSaveBar
          isVisible={isDirty}
          isSubmitting={isSubmitting || isAccountUpdating}
          isValid={isValid}
          isDirty={isDirty}
          errors={errors}
          onDiscard={handleDiscard}
          showNavigationWarning={blocker.state === 'blocked'}
          onCancelLeave={() => blocker.reset?.()}
          onConfirmLeave={() => blocker.proceed?.()}
        />
        <DebugZodErrors shouldDebug={false} />
      </form>
    </FormProvider>
  )
}
